/**
 * Bot mode-row wrapped around the manual order form in the Value Dashboard
 * trade ticket: [Manual | Pair MM | Penelope]. Pair MM is offered to managers/admins on a
 * game with both sibling Kalshi markets; it adds the game's away+home pair to
 * the in-relay vanilla MM slate (POST /api/vanilla/games, the two-leg sum-pair
 * bot behind /bots/vanilla). The row renders ONLY when an operator can drive
 * it AND there is a pair to add — otherwise the children (the existing order
 * form) render byte-for-byte unchanged. Default mode is Manual, so the
 * existing flow is untouched until an operator opts in; after that the tab the
 * viewer last chose is kept across games (R9, per browser).
 *
 * Penelope (the per-game Pinnacle maker bot) is offered to whoever holds the
 * Penelope grant, operator or not: one click attaches and arms her on this
 * game under the caller's own account (PenelopeTicket).
 *
 * The edge-spawn "Vanilla" tab that used to sit beside Pair MM (g10edge agents
 * via /api/edge) was removed with the g10edge lane; nothing here touches
 * /api/edge.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { apiFetch, useAuthStore } from '@/stores/authStore';
import { usePenelopeAccess, usePenelopeServiceOn } from '@/hooks/usePenelopeAccess';
import { PenelopeTicket } from '@/views/LiveGame/PenelopeTicket';
import type { GameRow } from './types';

type Mode = 'manual' | 'pairmm' | 'penelope';

/** The game's sibling Kalshi pair, for the Pair MM (in-relay sum-pair) mode. */
export interface PairMmTarget {
  eventTicker: string;
  tickerAway: string;
  tickerHome: string;
  exchangeIndexAway?: number;
  exchangeIndexHome?: number;
  awayCode: string;
  homeCode: string;
}

/** R9: the tab the viewer last chose, kept across games (the panel remounts per game, which always reset it to Manual —
 *  a Penelope trader re-clicked the tab on every game). A per-viewer convenience: unreadable storage means Manual. */
const TAB_KEY = 'g1.botMode.tab';
const readTab = (): Mode => {
  try {
    const v = localStorage.getItem(TAB_KEY);
    return v === 'penelope' || v === 'pairmm' ? v : 'manual';
  } catch { return 'manual'; }
};
const writeTab = (m: Mode): void => {
  try { localStorage.setItem(TAB_KEY, m); } catch { /* per-viewer convenience only */ }
};

export function BotModeSwitch({ pair, game, children, miniInTicket = true }: { pair?: PairMmTarget | null; game?: GameRow | null; children: ReactNode; miniInTicket?: boolean }) {
  const role = useAuthStore((s) => s.user?.role);
  const [chosen, setChosen] = useState<Mode>(readTab);
  const setMode = (m: Mode) => { setChosen(m); writeTab(m); };
  const penelopeAccess = usePenelopeAccess();
  const penelopeServiceOn = usePenelopeServiceOn(penelopeAccess);

  // Pair MM drives the manager-gated in-relay pair bot (/api/vanilla routes) —
  // a plain bot-access trader's POST would 403, so the row is operator-only.
  // No operator role or no sibling pair → existing ticket, completely unchanged.
  // Penelope is on for everyone unless a manager switched it off (opt-out since
  // 2026-10-08), so the tab is gated on the BOX instead: a relay without the
  // Penelope service (PENELOPE unset) gets no dead tab.
  const operator = role === 'manager' || role === 'admin';
  const penelope = penelopeAccess && !!game && penelopeServiceOn;
  if (!pair || !(operator || penelope)) return <>{children}</>;

  const modes: { value: Mode; label: string }[] = [
    { value: 'manual', label: 'Manual' },
    ...(operator ? [{ value: 'pairmm' as const, label: 'Pair MM' }] : []),
    ...(penelope ? [{ value: 'penelope' as const, label: 'Penelope' }] : []),
  ];
  // A remembered tab this game (or this viewer) does not offer reads as Manual.
  const mode: Mode = modes.some((m) => m.value === chosen) ? chosen : 'manual';

  return (
    <div>
      <div className="border-border flex border-b">
        {modes.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => setMode(m.value)}
            className={cn(
              'flex-1 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wider transition-colors',
              mode === m.value
                ? 'text-foreground border-foreground/60 border-b-2'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {m.label}
          </button>
        ))}
      </div>
      {mode === 'manual' ? children : mode === 'penelope' && game ? <PenelopeTicket game={game} miniInTicket={miniInTicket} /> : <PairMmForm pair={pair} />}
    </div>
  );
}

/** Pair MM: add this game's away+home pair to today's in-relay vanilla MM
 * slate. The relay validates the pair against the live Kalshi event and
 * windows it NOW → game start (or a fallback duration when the event has no
 * start-time anchor — the input appears only when the relay asks). */
function PairMmForm({ pair }: { pair: PairMmTarget }) {
  const [busy, setBusy] = useState(false);
  const [needsDuration, setNeedsDuration] = useState(false);
  const [durationMin, setDurationMin] = useState('120');
  const [status, setStatus] = useState<
    null | { kind: 'ok' } | { kind: 'exists' } | { kind: 'error'; msg: string }
  >(null);

  const deploy = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const res = await apiFetch('/api/vanilla/games', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          eventTicker: pair.eventTicker,
          tickerAway: pair.tickerAway,
          tickerHome: pair.tickerHome,
          awayCode: pair.awayCode || undefined,
          homeCode: pair.homeCode || undefined,
          ...(needsDuration && Number.isFinite(Number(durationMin))
            ? { fallbackDurationMin: Number(durationMin) }
            : {}),
        }),
      });
      if (res.ok) {
        setStatus({ kind: 'ok' });
      } else if (res.status === 409) {
        setStatus({ kind: 'exists' });
      } else {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        const msg = body?.error ?? `HTTP ${res.status}`;
        if (msg.includes('fallbackDurationMin')) setNeedsDuration(true);
        setStatus({ kind: 'error', msg });
      }
    } catch (err) {
      setStatus({ kind: 'error', msg: (err as Error).message });
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3 p-3">
      <div>
        <div className="text-foreground text-sm font-semibold">
          {pair.awayCode} @ {pair.homeCode}
        </div>
        <div className="text-muted-foreground font-mono text-[11px]">{pair.eventTicker}</div>
      </div>
      <p className="text-muted-foreground text-[11px]">
        Adds this game's away+home pair to today's Vanilla MM slate (the in-relay two-leg
        sum-pair bot). It quotes only while the day is ARMED on{' '}
        <Link to="/bots/vanilla" className="text-primary underline">
          /bots/vanilla
        </Link>
        , where size/caps/sum are set day-wide.
      </p>
      {needsDuration && (
        <label className="block">
          <span className="text-muted-foreground text-[11px]">
            No start-time anchor found — quote for (minutes)
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={5}
            max={720}
            value={durationMin}
            onChange={(e) => setDurationMin(e.target.value)}
            className="bg-background border-border text-foreground focus:border-primary mt-1 w-full rounded border px-2 py-1.5 text-sm focus:outline-none"
          />
        </label>
      )}
      {status?.kind === 'ok' || status?.kind === 'exists' ? (
        <div className="text-xs text-emerald-500">
          {status.kind === 'ok' ? 'Added to today’s slate.' : 'Already on today’s slate.'}{' '}
          <Link to="/bots/vanilla" className="underline">
            Open the Vanilla MM pane →
          </Link>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => void deploy()}
          className="bg-primary text-primary-foreground w-full rounded py-1.5 text-sm font-bold disabled:opacity-50"
        >
          {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Add to Vanilla MM slate'}
        </button>
      )}
      {status?.kind === 'error' && <p className="text-destructive text-xs">{status.msg}</p>}
    </div>
  );
}
